/**
 * Tailwind CSS implementation of the Skeleton component.
 * Uses Seeds skeleton CSS classes from skeleton.css.
 */

import React from "react";
import type { SkeletonProps } from "./SkeletonTypes";
import { getSkeletonVariant, radiusVar } from "./variant";

// Utility for merging class names properly
function cn(
  ...inputs: (string | undefined | null | false | Record<string, boolean>)[]
): string {
  const classes: string[] = [];

  for (const input of inputs) {
    if (!input) continue;

    if (typeof input === "string") {
      classes.push(input);
    } else if (typeof input === "object") {
      for (const [key, value] of Object.entries(input)) {
        if (value) {
          classes.push(key);
        }
      }
    }
  }

  return classes.join(" ");
}

/**
 * Converts a system-prop dimension (number → px, string passthrough) into a
 * value usable in an inline style. Matches how Box/styled-system renders these.
 */
const toDimension = (
  value: SkeletonProps["width"]
): string | number | undefined =>
  typeof value === "number" ? `${value}px` : (value as string | undefined);

export const SkeletonTailwind = React.forwardRef<HTMLDivElement, SkeletonProps>(
  ({ borderRadius, height, width, className, style, ...rest }, ref) => {
    const variant = getSkeletonVariant({ borderRadius, height, width });

    const skeletonStyle: React.CSSProperties = {
      width: toDimension(width),
      height: toDimension(height),
      ...style,
      // The shimmer/ring surface reads this for corner rounding; circular masks
      // its own shape via border-radius: 50% in skeleton.css.
      ["--skeleton-radius" as any]: radiusVar(borderRadius),
    };

    return (
      <div
        ref={ref}
        className={cn("seeds-skeleton", variant, className)}
        style={skeletonStyle}
        // `rest` is typed as the residual Box/styled-system props (SkeletonProps
        // extends TypeBoxProps); the hybrid gate (SkeletonHybrid) only routes
        // zero-styled-prop usage here, so at runtime `rest` carries plain DOM
        // attributes. Narrow the spread to keep the literal <div> type-safe —
        // ButtonTailwind sidesteps this by rendering to a Component cast as any.
        {...(rest as React.HTMLAttributes<HTMLDivElement>)}
      />
    );
  }
);

SkeletonTailwind.displayName = "SkeletonTailwind";
